import React, { useEffect, useState } from "react";
import "./AddWorkshops.css";
import { Flex, SafeArea } from "../../components/layout";
import { Button, Form, Input, Select, Title } from "../../components/ui";
import Editor from "../../components/common/editor/Editor";
import { db, storage } from "../../components/firebase/firebase";
import { setDoc, doc, getDoc, deleteDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { slugify } from "../../components/function";
import Notification from "../../components/ui/alert/Notification";
import { optimizeImage, replaceImageUrls } from "../../functions/image";
import { useNavigate, useParams } from "react-router-dom";
import { logEvent } from "../../utils/logger";

function AddWorkshops() {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [imageFiles, setImageFiles] = useState([]); // Stocke les images pour l'upload
  const [loading, setLoading] = useState(false);
  const [editorContent, setEditorContent] = useState();
  const [notification, setNotification] = useState(null);

  const navigate = useNavigate();

  const { id } = useParams();

  const fetchWorkshop = async () => {
    const docRef = doc(db, "workshops", id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      setTitle(data.title);
      setCategory(data.category);
      setEditorContent(data.content);
    } else {
      console.log("No such document!");
    }
  };

  useEffect(() => {
    if (id) {
      fetchWorkshop();
    }
  }, []);

  // Upload des images vers Firebase Storage après optimisation
  const uploadImagesToStorage = async () => {
    const uploadedImages = [];

    for (const img of imageFiles) {
      try {
        const optimizedImage = await optimizeImage(img.file); // ✅ Compression si nécessaire
        const storageRef = ref(
          storage,
          `workshops/${slugify(title)}/${Date.now()}_${img.file.name}`,
        );
        await uploadBytes(storageRef, optimizedImage);
        const downloadURL = await getDownloadURL(storageRef);
        uploadedImages.push({ oldUrl: img.url, newUrl: downloadURL });
      } catch (error) {
        logEvent("ERROR", "DB", "Workshop image upload failed", {
          error: error.message,
          stack: error.stack,
          fileName: img.file?.name,
          workshopTitle: title,
        });
        console.error("Erreur lors de l'upload :", error);
      }
    }

    return uploadedImages;
  };

  // Remplace les URLs locales par celles de Firebase

  // Sauvegarde de l’article dans Firestore avec images finales
  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (!title || !editorContent) {
      setLoading(false);
      alert("Le titre et le contenu sont obligatoires.");
      return;
    }

    try {
      const images = await uploadImagesToStorage();
      const finalContent = replaceImageUrls(editorContent, images);

      // Extraction de la première image
      const imgMatch = finalContent.match(/<img.*?src=["'](.*?)["']/);
      const picture = imgMatch ? imgMatch[1] : "";

      // Extraction d'une description textuelle (sans HTML et sans les textes dans les h4)
      let textContent = finalContent
        .replace(/<h4[^>]*>.*?<\/h4>/gi, "")
        .replace(/<[^>]*>?/gm, "")
        .trim();
      const description =
        textContent.length > 150
          ? textContent.substring(0, 147) + "..."
          : textContent;

      if (id && id !== slugify(title)) {
        await deleteDoc(doc(db, "workshops", id));
      }
      await setDoc(doc(db, "workshops", slugify(title)), {
        title,
        content: finalContent,
        category: category || "Bien-être",
        picture,
        description,
        createdAt: Date.now(),
      });

      setTitle("");
      setCategory("");
      setImageFiles([]);
      setEditorContent("");
      setNotification({
        type: "success",
        message: "Action réalisée avec succès.",
      });
      logEvent("INFO", "DB", id ? "Workshop updated" : "Workshop created", {
        slug: slugify(title),
        title,
        category: category || "Bien-être",
        imagesCount: images.length,
        descriptionLength: description.length,
        editedFrom: id || null,
      });
      if (id) {
        navigate("/workshops/manage");
      }
    } catch (error) {
      logEvent("ERROR", "DB", "Workshop save failed", {
        error: error.message,
        stack: error.stack,
        title,
        isEdit: !!id,
      });
      console.error("Erreur lors de la sauvegarde :", error);
      setNotification({ type: "error", message: "Échec de la sauvegarde." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <SafeArea className="AddWorkshops">
      <Notification
        state={notification}
        message={notification?.message || ""}
        type={notification?.type || "success"}
        onClose={() => setNotification(null)}
      />
      <Flex type="col" gap={50}>
        <Title level="h3">{id ? "Modifier" : "Ajouter"} un atelier</Title>
        <Form>
          <Flex type="col" gap={20}>
            <Input
              label="Titre"
              type="text"
              placeholder="Méditation, Yoga, etc."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <Select
              label="Catégorie"
              options={[
                { value: "Bien-être", name: "Bien-être" },
                {
                  value: "Expérimentations sur le terrain",
                  name: "Expérimentations sur le terrain",
                },
                { value: "Coaching", name: "Coaching" },
              ]}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            />
            <Editor
              label="Contenu"
              setImageFiles={setImageFiles}
              onChange={setEditorContent}
              value={editorContent}
            />
            <Flex type="end-center" className="ctn-btn" gap={15}>
              <Button skin="nobg" to={id ? "/workshops/manage" : "/"}>
                Annuler
              </Button>
              <Button onClick={handleSave} loading={loading}>
                Publier
              </Button>
            </Flex>
          </Flex>
        </Form>
      </Flex>
    </SafeArea>
  );
}

export default AddWorkshops;
